import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, Send, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success(t('contact.sent'));
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream pt-24" data-testid="contact-page">
      {/* Hero */}
      <section className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs uppercase tracking-[0.2em] text-gold mb-4 block">{t('contact.getInTouch')}</span>
            <h1 className="font-serif text-4xl md:text-5xl text-charcoal mb-4">{t('contact.title')}</h1>
            <p className="text-charcoal/70 max-w-2xl mx-auto">
              {t('contact.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-serif text-2xl text-charcoal mb-6">{t('contact.reachOut')}</h2>
              
              <div className="space-y-6 mb-12">
                <div className="flex items-start gap-4">
                  <div className="bg-burgundy p-3 rounded-xl">
                    <MapPin size={20} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-medium text-charcoal mb-1">{t('contact.address')}</h3>
                    <p className="text-charcoal/70">
                      109 N Beaumont Avenue #1078<br />
                      Kissimmee, FL 34741
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="bg-gold p-3 rounded-xl">
                    <Phone size={20} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-medium text-charcoal mb-1">{t('contact.phone')}</h3>
                    <p className="text-charcoal/70">(407) 216-2853</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="bg-purple p-3 rounded-xl">
                    <Mail size={20} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-medium text-charcoal mb-1">{t('contact.email')}</h3>
                    <p className="text-charcoal/70">livingfruitministry@gmail.com</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="bg-olive p-3 rounded-xl">
                    <Clock size={20} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-medium text-charcoal mb-1">{t('contact.serviceTimes')}</h3>
                    <p className="text-charcoal/70">
                      {t('contact.sundayWorship')}<br />
                      {t('contact.wednesdayBible')}<br />
                      {t('contact.saturdayPantry')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gold/10">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3510.4876!2d-81.4097!3d28.2984!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88dd810c3a7d0f3b%3A0x4a5f41c8f8d3d3d3!2s109%20N%20Beaumont%20Ave%2C%20Kissimmee%2C%20FL%2034741!5e0!3m2!1sen!2sus!4v1234567890"
                  width="100%"
                  height="300"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Living Fruit Ministry Location"
                  data-testid="google-map"
                />
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {submitted ? (
                <div className="bg-white rounded-3xl p-8 md:p-12 text-center shadow-lg border border-gold/10 h-full flex flex-col items-center justify-center">
                  <div className="w-16 h-16 bg-olive rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle size={32} className="text-white" />
                  </div>
                  <h3 className="font-serif text-2xl text-charcoal mb-4">{t('contact.sent')}</h3>
                  <p className="text-charcoal/70 mb-6">
                    {t('contact.thankYou')}
                  </p>
                  <Button
                    onClick={() => {
                      setSubmitted(false);
                      setFormData({ name: '', email: '', phone: '', message: '' });
                    }}
                    className="bg-burgundy hover:bg-purple text-white"
                  >
                    {t('contact.sendAnother')}
                  </Button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  className="bg-white rounded-3xl p-8 md:p-12 shadow-lg border border-gold/10"
                  data-testid="contact-form"
                >
                  <h2 className="font-serif text-2xl text-charcoal mb-6">{t('contact.sendMessage')}</h2>
                  
                  <div className="space-y-4">
                    <div>
                      <Label>{t('contact.fullName')}</Label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        required
                        className="bg-cream border-gold/20 focus:border-gold"
                        data-testid="contact-name"
                      />
                    </div>
                    
                    <div>
                      <Label>{t('contact.email')}</Label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        required
                        className="bg-cream border-gold/20 focus:border-gold"
                        data-testid="contact-email"
                      />
                    </div>
                    
                    <div>
                      <Label>{t('contact.phoneOptional')}</Label>
                      <Input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        className="bg-cream border-gold/20 focus:border-gold"
                        data-testid="contact-phone"
                      />
                    </div>
                    
                    <div>
                      <Label>{t('contact.message')}</Label>
                      <Textarea
                        value={formData.message}
                        onChange={(e) => setFormData({...formData, message: e.target.value})}
                        required
                        rows={5}
                        className="bg-cream border-gold/20 focus:border-gold"
                        data-testid="contact-message"
                      />
                    </div>
                    
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-burgundy hover:bg-purple text-white py-4"
                      data-testid="submit-contact"
                    >
                      {isSubmitting ? t('contact.sending') : (
                        <>
                          <Send size={18} className="mr-2" /> {t('contact.send')}
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
